import { useEffect, useState } from 'react';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    
    formData.forEach((value, key) => {
      data.append(key, value.toString());
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d6o1bheancq2eie81me0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: data.toString(),
      });

      if (response.ok) {
        alert('Message sent successfully!');
        form.reset();
      } else {
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      alert('An error occurred. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-sm' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-6 py-8 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img src="https://public.readdy.ai/ai/img_res/e3e2dce4-f045-4aed-a340-233c43346e4b.png" alt="BOBAK DOO FUTOG Logo" className="h-12 w-12 object-contain" />
            <h1 className="text-base font-bold tracking-wider text-gray-900">BOBAK DOO FUTOG</h1>
          </div>
          <nav className="flex items-center gap-10">
            <a href="#home" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Home</a>
            <a href="#about" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">About</a>
            <a href="#products" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Products</a>
            <a href="#contact" className="text-sm font-medium text-gray-700 hover:text-gray-900 transition-colors cursor-pointer">Contact</a>
          </nav>
        </div>
      </header>

      {/* Hero Section */}
      <section id="home" className="relative min-h-screen flex items-center bg-gradient-to-br from-gray-50 to-white pt-24">
        <div className="absolute inset-0 bg-gradient-to-b from-black/5 via-transparent to-transparent"></div>
        <div className="max-w-7xl mx-auto px-6 py-20 grid grid-cols-1 lg:grid-cols-2 gap-16 items-center relative z-10">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-7xl font-extrabold text-gray-900 leading-tight">
                Modern iOS<br />
                Applications for<br />
                Enhanced Security
              </h2>
            </div>
            <p className="text-lg text-gray-600 leading-relaxed font-light max-w-xl">
              We are a team of experts developing cutting-edge applications for the iOS platform. Our products, including VPN and Cleaner solutions, are designed to enhance user experience and improve the security of mobile devices.
            </p>
          </div>
          <div className="relative">
            <div className="rounded-3xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/f751fa6560b87f0f3102609e2739533d.jpg" 
                alt="iOS Security Applications" 
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section id="products" className="py-32 bg-gradient-to-b from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="mb-20">
            <p className="text-xs uppercase tracking-widest text-gray-500 mb-6">OUR PRODUCTS</p>
            <h3 className="text-6xl font-extrabold text-gray-900 leading-tight">
              Premium Solutions<br />
              for iOS Platform
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* VPN Card */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-shield-check-line text-5xl text-gray-900"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">VPN Security</h4>
              <p className="text-base text-gray-600 leading-relaxed">
                Protect your online privacy with our advanced VPN solution. Enjoy encrypted connections, secure browsing, and complete anonymity across all your iOS devices.
              </p>
            </div>

            {/* Cleaner Card */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-delete-bin-line text-5xl text-gray-900"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Smart Cleaner</h4>
              <p className="text-base text-gray-600 leading-relaxed">
                Optimize your device performance with intelligent storage management. Remove junk files, clear cache, and keep your iOS device running smoothly and efficiently.
              </p>
            </div>

            {/* Experience Card */}
            <div className="bg-white rounded-3xl p-12 shadow-lg hover:shadow-xl transition-shadow">
              <div className="w-14 h-14 flex items-center justify-center mb-8">
                <i className="ri-star-line text-5xl text-gray-900"></i>
              </div>
              <h4 className="text-2xl font-bold text-gray-900 mb-4">Seamless Experience</h4>
              <p className="text-base text-gray-600 leading-relaxed">
                Experience intuitive design and reliable performance. Our applications are built with user-centric approach, ensuring smooth operation and exceptional usability.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* About Section */}
      <section id="about" className="py-32 bg-gradient-to-b from-white to-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-xl">
                <img 
                  src="https://readdy.ai/api/search-image?query=Professional%20software%20development%20team%20workspace%20with%20modern%20computers%20displaying%20iOS%20app%20development%20code%20and%20interface%20design%20on%20screens%20in%20bright%20minimalist%20office%20environment%20with%20clean%20white%20walls%20and%20natural%20lighting%20showcasing%20technology%20innovation&width=600&height=750&seq=about-workspace-002&orientation=portrait" 
                  alt="Expert Development Team" 
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            
            <div className="space-y-8">
              <div className="inline-block px-6 py-2 border border-gray-300 rounded-full">
                <span className="text-xs uppercase tracking-widest text-gray-600">ABOUT US</span>
              </div>
              <h3 className="text-5xl font-extrabold text-gray-900 leading-tight">
                Expert Team<br />
                Building Modern<br />
                iOS Solutions
              </h3>
              <p className="text-lg text-gray-600 leading-relaxed font-light">
                Our team specializes in creating innovative applications that prioritize mobile security and performance optimization. With years of experience in iOS development, we deliver solutions that meet the highest standards of quality and user satisfaction.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed font-light">
                We combine technical expertise with creative design to build applications that not only protect your data but also enhance your daily mobile experience. Every product we create reflects our commitment to excellence and innovation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative h-[500px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://readdy.ai/api/search-image?query=Abstract%20panoramic%20gradient%20background%20with%20flowing%20purple%20blue%20and%20teal%20colors%20blended%20smoothly%20with%20geometric%20patterns%20and%20soft%20light%20rays%20creating%20modern%20technological%20atmosphere%20for%20premium%20iOS%20application%20showcase%20banner&width=1920&height=500&seq=cta-panoramic-003&orientation=landscape" 
            alt="Background" 
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/40"></div>
        </div>
        
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 flex flex-col justify-between py-20">
          <div className="mt-12">
            <h3 className="text-7xl font-bold text-white lowercase">enhance your</h3>
          </div>
          <div className="self-end text-right mb-12">
            <p className="text-xl text-white font-light leading-relaxed">
              Experience premium security<br />
              and performance on iOS
            </p>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-32 bg-gradient-to-b from-teal-50 to-teal-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h3 className="text-5xl font-extrabold text-gray-900 mb-6">Get in Touch</h3>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Have questions about our products? We'd love to hear from you. Send us a message and we'll respond as soon as possible.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <div className="bg-white rounded-3xl p-12 shadow-lg">
              <form onSubmit={handleSubmit} id="contact-form" data-readdy-form className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">Name</label>
                  <input 
                    type="text" 
                    id="name" 
                    name="name" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">Email</label>
                  <input 
                    type="email" 
                    id="email" 
                    name="email" 
                    required 
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm"
                  />
                </div>
                
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-2">Message</label>
                  <textarea 
                    id="message" 
                    name="message" 
                    rows={6} 
                    required 
                    maxLength={500}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all resize-none text-sm"
                  ></textarea>
                  <p className="text-xs text-gray-500 mt-1">Maximum 500 characters</p>
                </div>
                
                <button 
                  type="submit" 
                  className="w-full bg-gray-900 text-white py-4 rounded-lg font-medium hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap text-sm"
                >
                  Send Message
                </button>
              </form>
            </div>

            {/* Contact Information */}
            <div className="space-y-8">
              <div className="bg-white rounded-3xl p-12 shadow-lg">
                <h4 className="text-2xl font-bold text-gray-900 mb-8">Contact Information</h4>
                
                <div className="space-y-6">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-map-pin-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Address</p>
                      <p className="text-sm text-gray-600">RADE KONDICA 172</p>
                      <p className="text-sm text-gray-600">Futog, 408361</p>
                      <p className="text-sm text-gray-600">Serbia</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-mail-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Email</p>
                      <a href="mailto:info@bobakdoofutog.com" className="text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                        info@bobakdoofutog.com
                      </a>
                    </div>
                  </div>

                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0">
                      <i className="ri-phone-line text-2xl text-teal-600"></i>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-gray-900 mb-1">Phone</p>
                      <a href="tel:+12693903914" className="text-sm text-gray-600 hover:text-teal-600 transition-colors cursor-pointer">
                        +1 2693903914
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
            {/* Company Info */}
            <div className="lg:col-span-2 space-y-6">
              <div className="flex items-center gap-4">
                <img src="https://public.readdy.ai/ai/img_res/e3e2dce4-f045-4aed-a340-233c43346e4b.png" alt="BOBAK DOO FUTOG Logo" className="h-10 w-10 object-contain" />
                <h5 className="text-xl font-bold">BOBAK DOO FUTOG</h5>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                A team of experts developing modern applications for the iOS platform. Our products are designed to enhance user experience and improve the security of mobile devices.
              </p>
            </div>

            {/* Products */}
            <div>
              <h6 className="text-base font-bold mb-6">Products</h6>
              <ul className="space-y-4">
                <li><span className="text-sm text-gray-400 cursor-pointer hover:text-white transition-colors">VPN Security</span></li>
                <li><span className="text-sm text-gray-400 cursor-pointer hover:text-white transition-colors">Device Cleaner</span></li>
                <li><span className="text-sm text-gray-400 cursor-pointer hover:text-white transition-colors">Performance Tools</span></li>
              </ul>
            </div>

            {/* Company */}
            <div>
              <h6 className="text-base font-bold mb-6">Company</h6>
              <ul className="space-y-4">
                <li><a href="#about" className="text-sm text-gray-400 cursor-pointer hover:text-white transition-colors">About Us</a></li>
                <li><span className="text-sm text-gray-400 cursor-pointer hover:text-white transition-colors">Our Team</span></li>
                <li><span className="text-sm text-gray-400 cursor-pointer hover:text-white transition-colors">Expertise</span></li>
                <li><span className="text-sm text-gray-400 cursor-pointer hover:text-white transition-colors">Technology</span></li>
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h6 className="text-base font-bold mb-6">Contact</h6>
              <ul className="space-y-4">
                <li className="text-sm text-gray-400">
                  <span className="block font-medium text-gray-300">Address</span>
                  RADE KONDICA 172
                </li>
                <li className="text-sm text-gray-400">
                  <span className="block font-medium text-gray-300">Location</span>
                  Futog, 408361, Serbia
                </li>
                <li className="text-sm text-gray-400">
                  <span className="block font-medium text-gray-300">Email</span>
                  info@bobakdoofutog.com
                </li>
                <li className="text-sm text-gray-400">
                  <span className="block font-medium text-gray-300">Phone</span>
                  +1 2693903914
                </li>
              </ul>
            </div>
          </div>

          <div className="border-t border-gray-800 mt-16 pt-8 text-center">
            <p className="text-sm text-gray-500">&copy; 2025 BOBAK DOO FUTOG. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
